import './style.scss'
import { Button, Dropdown, Input, Menu, message, Modal, Tour } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { cdnApi } from '@/api'
import * as UserApi from '@/api/account'
import * as Api from '@/api/home'
import AiInteract from '@/assets/ai-interact.png'
import LogoTitleEn from '@/assets/logo-title-en.png'
import LogoTitle from '@/assets/logo-title.webp'
import Logo from '@/assets/logo.webp'
import { Home, Video } from '@/assets/svg'
import { ArrowDown } from '@/assets/svg/arrow-down'
import { LayoutAvatar } from '@/assets/svg/layout-avatar'
import { Study } from '@/assets/svg/study'
import { User } from '@/assets/svg/user'
import CheckLogin from '@/components/CheckLogin'
import CouponListModal from '@/components/Coupon/list'
import PayPointModal from '@/components/PayPointModal'
import { UserStore } from '@/global-states'
import { eventTracking, getLanguage, isZh, showPlanModal } from '@/libs/util'
import ContractListModal from '@/views/setting/components/ContractListModal'
import {
  AppstoreFilled,
  AudioFilled,
  CopyrightCircleFilled,
  PayCircleFilled,
  RightOutlined,
  TrademarkCircleFilled
} from '@ant-design/icons'
import { googleLogout } from '@react-oauth/google'

import type { MenuProps } from 'antd'
type MenuItem = Required<MenuProps>['items'][number]

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  extra?: React.ReactNode,
  type?: string
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    extra,
    type
  } as MenuItem
}

const LayoutMenu: React.FC = () => {
  const { t: translate } = useTranslation(['layout'])
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { uid, userPackage, phone, studyCenterClick, systemGuideStep } = UserStore
  const [data, setData] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const [showInteract, setShowInteract] = useState(false)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [couponListOpen, setCouponListOpen] = useState(false)
  const [code, setCode] = useState('')
  const [contractListModalOpen, setContractListModalOpen] = useState(false)
  const [contractList, setContractList] = useState([])
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const guideRef = useRef<any>()

  const steps: any = [
    {
      title: translate('layout.guide_title'),
      target: () => guideRef.current,
      closeIcon: false,
      placement: 'top'
    }
  ]

  const selectedKeys = useMemo(() => {
    const pathWithoutQuery = pathname.split('?')[0] // Remove query parameters
    const promoterMatch = matchPath('/p/:promoterCode', pathWithoutQuery)
    const inviteMatch = matchPath('/i/:inviteCode', pathWithoutQuery)
    const matchs = ['/', '/home']

    if (
      matchs.includes(pathWithoutQuery) ||
      pathWithoutQuery.includes('/video-generator') ||
      pathWithoutQuery.includes('/podcast') ||
      pathWithoutQuery.includes('/picture') ||
      pathWithoutQuery.includes('/ansme') ||
      pathWithoutQuery.includes('/cartoon') ||
      promoterMatch ||
      inviteMatch
    ) {
      return ['/']
    }
    return [pathWithoutQuery]
  }, [pathname])

  const vip = useMemo(() => {
    return !!userPackage?.membership_expire_time
  }, [userPackage])

  const vipExpired = useMemo(() => {
    return !!userPackage?.membership_expired
  }, [userPackage])

  useEffect(() => {
    getInteractConfig()
    getContractList()
  }, [])

  const showMarketNew = useMemo(() => {
    if (data?.length) {
      return true
    }
    return false
  }, [data])

  const getContractList = async () => {
    const res = await Api.getContractList()
    setContractList(res?.list || [])
  }

  const getInteractConfig = async () => {
    const res = await cdnApi.get(`hiflys/interact/config.json?v=${Date.now()}`)
    if (res.open === true) {
      setShowInteract(true)
    }
  }

  const showItems = useMemo(() => {
    return [
      getItem(
        translate('layout.creation'),
        '/',
        undefined,
        [
          getItem(translate('layout.homepage'), '/', <Home className="icon" />, undefined),
          getItem(translate('layout.digital_human'), '/avatar', <LayoutAvatar className="avatar" />, undefined),
          getItem(translate('layout.voice_clone'), '/clone', <CopyrightCircleFilled className="clone" />, undefined),
          getItem(
            translate('layout.digital_market'),
            '/market/avatar',
            <AppstoreFilled className="market" />,
            undefined,
            undefined
          ),
          getItem(translate('layout.voice_market'), '/market/voice', <AudioFilled className="icon-voice" />, undefined)
        ],
        undefined,
        'group'
      ),
      { type: 'divider' },
      getItem(
        translate('layout.management'),
        '/',
        undefined,
        [
          getItem(translate('layout.works_management'), '/video', <Video className="icon" />, undefined),
          getItem(
            translate('layout.free_points'),
            '/invite',
            <TrademarkCircleFilled className="icon-trade" />,
            undefined
          ),
          getItem(
            translate('layout.promotion'),
            '/promoter',
            <PayCircleFilled className="icon" />,
            undefined,
            <div className={`menu-tag ${getLanguage()}`}>{translate('layout.earn_money')}</div>
          ),
          getItem(translate('layout.study_center'), `/study/index.html`, <Study className="study" />, undefined),
          getItem(translate('layout.personal_center'), '/setting', <User className="icon-setting" />, undefined)
        ],
        undefined,
        'group'
      )
    ]
  }, [studyCenterClick, showMarketNew, translate])

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    const [merchandises] = await Promise.all([Api.getMerchandisesV2()])
    setData([...(merchandises?.list || []).filter((l: any) => l.status !== 2)])
  }

  const jumpToStudyCenter = () => {
    localStorage.setItem('studyCenter', 'true')
    UserStore.studyCenterClick = true
    window.open(`/study/index.html?v=${Date.now()}`)
  }

  const onClick: MenuProps['onClick'] = (e) => {
    if (e.key === '/study/index.html') {
      jumpToStudyCenter()
    } else {
      navigate(e.key)
    }
  }

  const toCreateVideo = () => {
    if (systemGuideStep >= 1) {
      eventTracking('SystemGuideNextStep1')
      localStorage.setItem('systemGuideRead', '1')
      UserStore.systemGuideStep = 2
    } else {
      eventTracking('CreateDisplayClick')
    }
    navigate('/create-video')
  }

  const toInteract = () => {
    navigate('/digital')
  }

  const toLogin = () => {
    if (!isZh()) {
      googleLogout()
    }
    UserStore.loginOut()
    navigate('/login')
  }

  const toHome = () => {
    navigate('/')
  }

  const onConvertCouponClick = () => {
    eventTracking('CreditsRedemption')
    setConvertModalOpen(true)
    setCode('')
  }

  const useCode = async () => {
    await UserApi.useCode({ redeem_code: code })
    setConvertModalOpen(false)
    message.success(translate('layout.exchange_success'))
    UserStore.updateUserPackage()
  }

  const toAddress = (link: string) => {
    window.open(link)
  }

  const toPayPoint = () => {
    eventTracking('RechargeCredits')
    setPayPointModalOpen(true)
  }

  return (
    <div className="layout-menu">
      <div className="layout-menu-header" onClick={toHome}>
        <img className="logo" src={Logo} />
        {isZh() ? <img className="logo-title" src={LogoTitle} /> : <img className="logo-title-en" src={LogoTitleEn} />}
      </div>

      <div className="layout-menu-center">
        <Menu
          theme="light"
          onClick={onClick}
          style={{ width: 230 }}
          selectedKeys={selectedKeys}
          mode="inline"
          items={showItems as any}
        />

        <Button ref={guideRef} className="create-video" type="primary" onClick={toCreateVideo}>
          {translate('layout.create_works')}
        </Button>

        {showInteract && (
          <CheckLogin>
            <img className="interact" src={AiInteract} onClick={toInteract} />
          </CheckLogin>
        )}
      </div>

      <div className="layout-menu-bottom">
        {!uid ? (
          <div className="unlogin">
            <div className="left">
              <div className="free">{translate('layout.guest_mode')}</div>
              <div className="desc">{translate('layout.not_logged_in')}</div>
            </div>
            <div className="right">
              <CheckLogin>
                <Button className="btn-login" type="primary" onClick={toLogin}>
                  {translate('layout.login')}
                </Button>
              </CheckLogin>
            </div>
          </div>
        ) : (
          <Dropdown
            placement="top"
            overlayClassName="customer-dropdown"
            trigger={['click']}
            open={open}
            dropdownRender={() => {
              return (
                <div className="dropdown-user" onClick={() => setOpen(false)}>
                  <div className="header">{translate('layout.hi')}</div>
                  <div className="phone">{phone?.replace(/^(.{3}).*?(.{3})$/i, '$1****$2')}</div>
                  {vip ? (
                    <>
                      {vipExpired ? (
                        <div className="level red">{translate('layout.member_expired')}</div>
                      ) : (
                        <div className="level gradient">
                          {userPackage?.current_membership_package_title}
                          {translate('layout.member')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="level gray">{translate('layout.not_member')}</div>
                  )}

                  <div className="menu-list">
                    <div className="item" onClick={() => setCouponListOpen(true)}>
                      <label>{translate('layout.my_coupons')}</label>
                      <RightOutlined />
                    </div>
                    <div className="item" onClick={onConvertCouponClick}>
                      <label>{translate('layout.points_exchange')}</label>
                      <RightOutlined />
                    </div>
                    {!!contractList?.length && (
                      <div className="item" onClick={() => setContractListModalOpen(true)}>
                        <label>{translate('layout.my_contracts')}</label>
                        <RightOutlined />
                      </div>
                    )}

                    <div className="item" onClick={toAddress.bind(this, '/privacy_agreement.html')}>
                      <label>{translate('layout.privacy_policy')}</label>
                      <RightOutlined />
                    </div>
                    <div className="item" onClick={toAddress.bind(this, '/eula.html')}>
                      <label>{translate('layout.user_agreement')}</label>
                      <RightOutlined />
                    </div>
                  </div>
                  <Button onClick={showPlanModal} type="primary">
                    {vip
                      ? vipExpired
                        ? translate('layout.renew_now')
                        : translate('layout.renew_upgrade')
                      : translate('layout.become_member')}
                  </Button>
                  <Button onClick={toPayPoint} color="default" variant="outlined">
                    {translate('layout.recharge_points')}
                  </Button>
                  <div className="switch" onClick={toLogin}>
                    {translate('layout.logout')}
                  </div>
                </div>
              )
            }}
            onOpenChange={(open) => {
              setOpen(open)
            }}
          >
            <div className="logined">
              <div className="left">
                <div className="header">{translate('layout.hi')}</div>
                <div className="detail">
                  <div className="phone">{phone?.replace(/^(.{3}).*?(.{3})$/i, '$1****$2')}</div>
                  {vip ? (
                    <>
                      {vipExpired ? (
                        <div className="title gray">{translate('layout.member_expired')}</div>
                      ) : (
                        <div className="title gradient">
                          {userPackage?.current_membership_package_title}
                          {translate('layout.member')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="title gray">{translate('layout.not_member')}</div>
                  )}
                </div>
              </div>

              <div className="right">
                <ArrowDown />
              </div>
            </div>
          </Dropdown>
        )}
      </div>

      <CouponListModal
        open={couponListOpen}
        onCancel={() => setCouponListOpen(false)}
        toUseCoupon={showPlanModal}
        changeOpen={(t) => setCouponListOpen(t)}
      />

      <Modal
        okText={translate('layout.exchange_now')}
        cancelText={translate('layout.cancel')}
        title={translate('layout.enter_code')}
        onCancel={() => setConvertModalOpen(false)}
        open={convertModalOpen}
        okButtonProps={{ disabled: !code }}
        onOk={useCode}
      >
        <div className="convert-content">
          <Input value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
      </Modal>

      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />

      <ContractListModal open={contractListModalOpen} onCancel={() => setContractListModalOpen(false)} />

      <Tour
        open={systemGuideStep === 1}
        closeIcon={null}
        steps={steps}
        mask={{
          color: 'rgba(0, 0, 0, 0.7)'
        }}
      />
    </div>
  )
}

export default LayoutMenu
